import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { BulbOutlined, CheckOutlined, CloseOutlined, SaveOutlined } from "@ant-design/icons";
import { ColorPicker } from "antd";
import { useRecentColors } from '../../hooks/useRecentColors';
import { useEyeDropper } from '../../hooks/useEyeDropper';
import { createColorPickerPanel, EyeDropperIcon } from '../../utils/colorPickerPanel';
import {
  getSavedPalettes,
  savePalette,
  removeSavedPalette,
  MAX_SAVED_PALETTES,
  SAVED_PALETTES_UPDATED_EVENT,
} from '../../utils/savedPalettes';
import { transformColor } from "../../../core/utils/generalUtils";
import { typography, surfaces, borders, textColors } from "../../../design-system/tokens";

/*
MultipleColorsField – visual editor for color arrays.
Renders each color as a chip with a color picker input and remove button.
Allows adding new colors and removing existing ones.
*/

// Predefined color combinations
const COLOR_SUGGESTIONS = [
  {
    name: "Ocean Blues",
    colors: ["#0ea5e9", "#0284c7", "#0369a1", "#075985"]
  },
  {
    name: "Sunset Warm",
    colors: ["#f59e0b", "#f97316", "#ef4444", "#dc2626"]
  },
  {
    name: "Forest Greens",
    colors: ["#10b981", "#059669", "#047857", "#065f46"]
  },
  {
    name: "Purple Gradients",
    colors: ["#8b5cf6", "#7c3aed", "#6d28d9", "#5b21b6"]
  },
  {
    name: "Monochrome",
    colors: ["#f8fafc", "#cbd5e1", "#64748b", "#1e293b"]
  },
  {
    name: "Earth Tones",
    colors: ["#d97706", "#92400e", "#451a03", "#78716c"]
  },
  {
    name: "Cool Pastels",
    colors: ["#a7f3d0", "#bfdbfe", "#ddd6fe", "#fce7f3"]
  },
  {
    name: "Bold Neons",
    colors: ["#00ff88", "#00ccff", "#ff00aa", "#ffaa00"]
  },
  {
    name: "Cherry Blossom",
    colors: ["#fdf2f8", "#fce7f3", "#f9a8d4", "#ec4899"]
  },
  {
    name: "Autumn Leaves",
    colors: ["#fed7aa", "#fdba74", "#f97316", "#ea580c"]
  },
  {
    name: "Midnight",
    colors: ["#1e1b4b", "#312e81", "#3730a3", "#4338ca"]
  },
  {
    name: "Coral Reef",
    colors: ["#fed7d7", "#fbb6ce", "#f687b3", "#ed64a6"]
  },
  {
    name: "Arctic Ice",
    colors: ["#f0f9ff", "#e0f2fe", "#7dd3fc", "#0284c7"]
  },
  {
    name: "Golden Hour",
    colors: ["#fef3c7", "#fde68a", "#f59e0b", "#d97706"]
  },
  {
    name: "Lavender Fields",
    colors: ["#f3e8ff", "#e9d5ff", "#c084fc", "#9333ea"]
  },
  {
    name: "Emerald City",
    colors: ["#d1fae5", "#a7f3d0", "#34d399", "#059669"]
  },
  {
    name: "Rose Gold",
    colors: ["#fdf2f8", "#fcce5f", "#e879f9", "#be185d"]
  },
  {
    name: "Volcano",
    colors: ["#451a03", "#7c2d12", "#dc2626", "#fca5a5"]
  },
  {
    name: "Tropical",
    colors: ["#065f46", "#059669", "#10b981", "#6ee7b7"]
  },
  {
    name: "Galaxy",
    colors: ["#0c0a09", "#1c1917", "#6366f1", "#a855f7"]
  },
  {
    name: "Desert Sand",
    colors: ["#fef7cd", "#fde047", "#eab308", "#a16207"]
  },
  {
    name: "Deep Sea",
    colors: ["#0f172a", "#1e293b", "#0ea5e9", "#38bdf8"]
  },
  {
    name: "Spring Garden",
    colors: ["#f7fee7", "#bef264", "#65a30d", "#365314"]
  },
  {
    name: "Cotton Candy",
    colors: ["#fef7ff", "#f3e8ff", "#d8b4fe", "#c084fc"]
  },
  {
    name: "Charcoal",
    colors: ["#f8fafc", "#e2e8f0", "#475569", "#0f172a"]
  },
  {
    name: "Peacock",
    colors: ["#164e63", "#0891b2", "#06b6d4", "#67e8f9"]
  },
  {
    name: "Wine & Roses",
    colors: ["#450a0a", "#7f1d1d", "#dc2626", "#fca5a5"]
  },
  {
    name: "Mint Chocolate",
    colors: ["#0c0a09", "#365314", "#16a34a", "#bbf7d0"]
  },
  {
    name: "Royal Purple",
    colors: ["#1e1b4b", "#4338ca", "#8b5cf6", "#c4b5fd"]
  },
  {
    name: "Sunrise",
    colors: ["#451a03", "#ea580c", "#fb923c", "#fed7aa"]
  },
  {
    name: "Neon Noir",
    colors: ["#00e5ff", "#8a2be2", "#1a1033", "#0f0f1a"]
  },
  {
    name: "Cyberpunk Night",
    colors: ["#00ffee", "#ff007a", "#1b1f3b", "#0a0f1f"]
  },
  {
    name: "Midnight Aurora",
    colors: ["#22d3ee", "#06b6d4", "#102a43", "#0b1020"]
  },
  {
    name: "Electric Orchid",
    colors: ["#f0abfc", "#a21caf", "#3b0d4f", "#1a1025"]
  },
  {
    name: "Deep Ocean Neon",
    colors: ["#06b6d4", "#22c55e", "#0b2d39", "#06141b"]
  },
  {
    name: "Galaxy Fiesta",
    colors: ["#f59e0b", "#9333ea", "#1f1147", "#0b1221"]
  },
  {
    name: "Vaporwave Dusk",
    colors: ["#22d3ee", "#ec4899", "#312e81", "#111827"]
  },
  {
    name: "Emerald Night",
    colors: ["#34d399", "#10b981", "#064e3b", "#0b1a13"]
  },
  {
    name: "Ruby Eclipse",
    colors: ["#f97316", "#ef4444", "#3f0d0d", "#1b0a0a"]
  },
  {
    name: "Sapphire Storm",
    colors: ["#60a5fa", "#38bdf8", "#1e3a8a", "#0b1220"]
  },
  {
    name: "Dark Carnival",
    colors: ["#a855f7", "#e11d48", "#3f1d2e", "#1c1917"]
  },
  {
    name: "Jewel Night",
    colors: ["#0ea5e9", "#22c55e", "#1e293b", "#0f172a"]
  },
  {
    name: "Shadow Spectrum",
    colors: ["#3b82f6", "#f43f5e", "#111827", "#0f1115"]
  },
  {
    name: "Terracotta",
    colors: ["#e07a5f", "#c1553a", "#9b4230", "#6b3024"]
  },
  {
    name: "Nordic Frost",
    colors: ["#ecfeff", "#cffafe", "#67e8f9", "#22d3ee"]
  },
  {
    name: "Art Deco",
    colors: ["#fef3c7", "#d4a843", "#292524", "#1c1917"]
  },
  {
    name: "Retro 80s",
    colors: ["#ff6b9d", "#c44dff", "#00d4ff", "#ffe45e"]
  },
  {
    name: "Coastal Breeze",
    colors: ["#e0f7fa", "#80deea", "#26c6da", "#00838f"]
  },
  {
    name: "Moody Blues",
    colors: ["#1a237e", "#283593", "#3949ab", "#5c6bc0"]
  },
  {
    name: "Blush & Cream",
    colors: ["#fef7f0", "#fce4ec", "#f8bbd0", "#e91e63"]
  },
  {
    name: "Industrial",
    colors: ["#9e9e9e", "#757575", "#424242", "#212121"]
  },
  {
    name: "Vintage Paper",
    colors: ["#fdf6e3", "#eee8d5", "#d4c5a9", "#93836c"]
  },
  {
    name: "Rainforest",
    colors: ["#004d40", "#00695c", "#00897b", "#4db6ac"]
  },
  {
    name: "Twilight Zone",
    colors: ["#1a1a2e", "#16213e", "#0f3460", "#533483"]
  },
  {
    name: "Candy Shop",
    colors: ["#ff6f91", "#ff9671", "#ffc75f", "#f9f871"]
  },
  {
    name: "Steel & Chrome",
    colors: ["#b0bec5", "#78909c", "#546e7a", "#37474f"]
  },
  {
    name: "Plum Harvest",
    colors: ["#4a148c", "#6a1b9a", "#8e24aa", "#ce93d8"]
  },
  {
    name: "Sage & Stone",
    colors: ["#e8f0e4", "#b5c7a3", "#7d9461", "#4a5d3a"]
  },
  {
    name: "Amber Glow",
    colors: ["#fff3e0", "#ffe0b2", "#ffb74d", "#e65100"]
  },
  {
    name: "Dark Academia",
    colors: ["#3e2723", "#4e342e", "#6d4c41", "#a1887f"]
  },
  {
    name: "Celestial",
    colors: ["#0d1b2a", "#1b2838", "#415a77", "#778da9"]
  },
  {
    name: "Zen Garden",
    colors: ["#f5f0eb", "#d4c5b9", "#a8978a", "#7d6b5d"]
  },
  {
    name: "Burnt Sienna",
    colors: ["#bf360c", "#d84315", "#e64a19", "#ff7043"]
  },
  {
    name: "Frost & Fire",
    colors: ["#e3f2fd", "#42a5f5", "#ef5350", "#b71c1c"]
  },
  {
    name: "Olive Grove",
    colors: ["#f1f8e9", "#aed581", "#689f38", "#33691e"]
  },
  {
    name: "Mauve Dream",
    colors: ["#f3e5f5", "#e1bee7", "#ba68c8", "#8e24aa"]
  },
  {
    name: "Teal & Coral",
    colors: ["#e0f2f1", "#4db6ac", "#ff8a65", "#bf360c"]
  },
  {
    name: "Watercolor Wash",
    colors: ["#fce4ec", "#e3f2fd", "#e8f5e9", "#fff9c4"]
  }
];

// Approximate popup footprint so we can choose a safer placement before AntD renders it.
const DEFAULT_PICKER_PLACEMENT = "bottomLeft";
const COLOR_PICKER_POPUP_WIDTH = 336;
const COLOR_PICKER_BASE_HEIGHT = 360;
const COLOR_PICKER_RECENT_SECTION_HEIGHT = 96;
const COLOR_PICKER_VIEWPORT_MARGIN = 16;

// Normalize any color token (e.g., "slate-700") or rgba to HEX for UI controls
const normalizeColorValue = (color) => {
  if (!color) return "#ffffff";
  if (typeof color === "string" && color.startsWith("#")) return color;
  try {
    const hex = transformColor(color, 100, "hex");
    return typeof hex === "string" && hex ? hex : "#ffffff";
  } catch (_) {
    return "#ffffff";
  }
};

const ensureFourColors = (arr) => {
  // Create a copy to avoid mutating props - critical to prevent render loops
  let newArr = Array.isArray(arr) ? [...arr] : ["#ffffff"];
  while (newArr.length < 4) newArr.push("#ffffff");
  if (newArr.length > 4) newArr = newArr.slice(0, 4);
  return newArr;
};

const normalizePaletteColors = (palette) => ensureFourColors(palette).map(normalizeColorValue);

const getPaletteSignature = (palette) => (
  Array.isArray(palette) ? normalizePaletteColors(palette).join("|") : null
);

const getPickerPlacement = ({ triggerRect, viewportWidth, viewportHeight, hasRecentColors }) => {
  if (!triggerRect || !viewportWidth || !viewportHeight) {
    return DEFAULT_PICKER_PLACEMENT;
  }

  const estimatedPopupHeight = COLOR_PICKER_BASE_HEIGHT + (
    hasRecentColors ? COLOR_PICKER_RECENT_SECTION_HEIGHT : 0
  );

  const spaceBelow = viewportHeight - triggerRect.bottom - COLOR_PICKER_VIEWPORT_MARGIN;
  const spaceAbove = triggerRect.top - COLOR_PICKER_VIEWPORT_MARGIN;
  const spaceRight = viewportWidth - triggerRect.right - COLOR_PICKER_VIEWPORT_MARGIN;
  const spaceLeft = triggerRect.left - COLOR_PICKER_VIEWPORT_MARGIN;

  if (spaceBelow >= estimatedPopupHeight) {
    return "bottomLeft";
  }

  if (spaceAbove >= estimatedPopupHeight) {
    return "topLeft";
  }

  if (spaceRight >= COLOR_PICKER_POPUP_WIDTH || spaceRight >= spaceLeft) {
    return "rightTop";
  }

  if (spaceLeft >= COLOR_PICKER_POPUP_WIDTH) {
    return "leftTop";
  }

  return spaceBelow >= spaceAbove ? "bottomLeft" : "topLeft";
};

export const MultipleColorsField = ({
  value,
  onChange,
  label,
  help,
  infoText,
  paletteStorageScope,
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [openColorIndex, setOpenColorIndex] = useState(null);
  const [pickerPlacement, setPickerPlacement] = useState(DEFAULT_PICKER_PLACEMENT);
  const [savedPalettes, setSavedPalettes] = useState(() => getSavedPalettes(paletteStorageScope));
  const dropdownRef = useRef(null);
  const suggestionsButtonRef = useRef(null);
  const colorButtonRefs = useRef([]);
  const [suggestionsDropdown, setSuggestionsDropdown] = useState({
    openUp: false,
    maxHeight: 320
  });
  const { 
    recentColors,
    setPendingColor, 
    addPendingColor, 
    addColorImmediately,
    currentPageColors,
    currentPage,
    totalPages,
    goToNextPage,
    goToPrevPage
  } = useRecentColors();
  const { isSupported: isEyeDropperSupported, isActive: isEyeDropperActive, pickColor } = useEyeDropper();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions]);

  // Keep suggestions dropdown within viewport by limiting height and flipping placement if needed
  useEffect(() => {
    if (!showSuggestions) return;

    const updateSuggestionsDropdown = () => {
      const btn = suggestionsButtonRef.current;
      if (!btn) return;

      const rect = btn.getBoundingClientRect();
      const margin = 12;
      const spaceBelow = window.innerHeight - rect.bottom - margin;
      const spaceAbove = rect.top - margin;

      const shouldOpenUp = spaceBelow < 240 && spaceAbove > spaceBelow;
      const availableSpace = shouldOpenUp ? spaceAbove : spaceBelow;

      // Cap height for UX, but never exceed available viewport space
      const maxHeight = Math.max(120, Math.min(420, Math.max(0, availableSpace)));

      setSuggestionsDropdown({
        openUp: shouldOpenUp,
        maxHeight: maxHeight || 200
      });
    };

    // Run after paint so DOM measurements are accurate
    const raf = window.requestAnimationFrame(updateSuggestionsDropdown);
    window.addEventListener("resize", updateSuggestionsDropdown);
    window.addEventListener("scroll", updateSuggestionsDropdown, true);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", updateSuggestionsDropdown);
      window.removeEventListener("scroll", updateSuggestionsDropdown, true);
    };
  }, [showSuggestions]);

  const resolvePickerPlacement = useCallback((index) => {
    const triggerElement = colorButtonRefs.current[index];

    if (!triggerElement || typeof window === "undefined") {
      return DEFAULT_PICKER_PLACEMENT;
    }

    return getPickerPlacement({
      triggerRect: triggerElement.getBoundingClientRect(),
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      hasRecentColors: recentColors.length > 0
    });
  }, [recentColors.length]);

  useEffect(() => {
    if (openColorIndex === null) return undefined;

    const updatePlacement = () => {
      setPickerPlacement(resolvePickerPlacement(openColorIndex));
    };

    const raf = window.requestAnimationFrame(updatePlacement);
    window.addEventListener("resize", updatePlacement);
    window.addEventListener("scroll", updatePlacement, true);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", updatePlacement);
      window.removeEventListener("scroll", updatePlacement, true);
    };
  }, [openColorIndex, resolvePickerPlacement]);

  useEffect(() => {
    setSavedPalettes(getSavedPalettes(paletteStorageScope));

    const handleSavedPalettesUpdate = (event) => {
      if (event?.detail?.scopeKey !== (paletteStorageScope || "global")) return;
      setSavedPalettes(event.detail.palettes || []);
    };

    window.addEventListener(SAVED_PALETTES_UPDATED_EVENT, handleSavedPalettesUpdate);
    return () => {
      window.removeEventListener(SAVED_PALETTES_UPDATED_EVENT, handleSavedPalettesUpdate);
    };
  }, [paletteStorageScope]);

  const colors = ensureFourColors(value);
  const normalizedCurrentPalette = useMemo(
    () => colors.map(normalizeColorValue),
    [colors],
  );
  const currentPaletteSignature = useMemo(
    () => getPaletteSignature(normalizedCurrentPalette),
    [normalizedCurrentPalette],
  );
  const savedPalettesSignatures = useMemo(
    () => new Set(savedPalettes.map(getPaletteSignature).filter(Boolean)),
    [savedPalettes],
  );
  const isCurrentPaletteSaved = savedPalettesSignatures.has(currentPaletteSignature);

  const paletteSuggestions = useMemo(() => {
    return COLOR_SUGGESTIONS.filter(
      (suggestion) => !savedPalettesSignatures.has(getPaletteSignature(suggestion.colors)),
    );
  }, [savedPalettesSignatures]);

  const updateColor = (index, newColor) => {
    const updatedColors = [...colors];
    updatedColors[index] = newColor;
    onChange(updatedColors);
    setPendingColor(newColor);
  };

  const handleEyeDropperPickForIndex = useCallback(async (index) => {
    // Close the color picker popup so it doesn't overlay the sampling area
    setOpenColorIndex(null);
    // Let the popup close animation finish before entering eyedropper mode
    await new Promise(resolve => setTimeout(resolve, 150));

    const hex = await pickColor();
    if (hex) {
      const updatedColors = [...colors];
      updatedColors[index] = hex;
      onChange(updatedColors);
      addColorImmediately(hex);
    }
  }, [pickColor, colors, onChange, addColorImmediately]);

  const applySuggestion = (suggestionColors) => {
    onChange(suggestionColors);
    // Add all colors from suggestion to recent colors immediately
    suggestionColors.forEach(color => {
      addColorImmediately(color);
    });
    setShowSuggestions(false);
  };

  const handleSavePalette = () => {
    const updatedPalettes = savePalette(paletteStorageScope, normalizedCurrentPalette);
    if (updatedPalettes) {
      setSavedPalettes(updatedPalettes);
    }
  };

  const handleRemovePalette = (paletteIndex) => {
    const updatedPalettes = removeSavedPalette(paletteStorageScope, paletteIndex);
    if (updatedPalettes) {
      setSavedPalettes(updatedPalettes);
    }
  };

  return (
    <div className="field-wrapper">
      {/* Label */}
      {label && (
        <div className="mb-2">
          <label className={`${typography.label} ${textColors.secondary}`}>{label}</label>
        </div>
      )}

      {/* Color swatches — compact grouped card with hex labels */}
      <div className={`rounded-xl border ${borders.default} ${surfaces.secondary} p-2`}>
        <div className="flex justify-end mb-1">
          <button
            type="button"
            onClick={handleSavePalette}
            disabled={isCurrentPaletteSaved}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md transition-colors ${typography.caption} ${
              isCurrentPaletteSaved
                ? "text-green-600 dark:text-green-400 cursor-default"
                : `${textColors.secondary} hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer`
            }`}
            title={isCurrentPaletteSaved ? "This palette is already saved" : "Save this palette"}
          >
            {isCurrentPaletteSaved ? (
              <CheckOutlined style={{ fontSize: 11 }} />
            ) : (
              <SaveOutlined style={{ fontSize: 11 }} />
            )}
            {isCurrentPaletteSaved ? "Saved" : "Save Palette"}
          </button>
        </div>
        <div className="flex gap-2 items-start justify-center">
          {colors.map((color, index) => {
            const isOpen = openColorIndex === index;
            const hexNormalized = normalizeColorValue(color);

            const handleColorChange = (colorObj) => {
              const hexColor = colorObj.toHexString();
              updateColor(index, hexColor);
            };

            const handleColorSelect = (hex) => {
              updateColor(index, hex);
              setOpenColorIndex(null); // Close the picker
            };

            const panelRender = createColorPickerPanel({
              currentValue: hexNormalized,
              onColorSelect: handleColorSelect,
              recentColors,
              currentPageColors,
              currentPage,
              totalPages,
              goToNextPage,
              goToPrevPage,
              addColorImmediately,
              onEyeDropperPick: () => handleEyeDropperPickForIndex(index),
              isEyeDropperSupported,
            });

            return (
              <div key={index} className="flex min-w-0 flex-col items-center gap-1">
                <ColorPicker
                  value={hexNormalized}
                  onChange={handleColorChange}
                  panelRender={panelRender}
                  placement={pickerPlacement}
                  open={isOpen}
                  onOpenChange={(open) => {
                    if (open) {
                      setPickerPlacement(resolvePickerPlacement(index));
                      setOpenColorIndex(index);
                      return;
                    }

                    setOpenColorIndex(null);
                    addPendingColor();
                  }}
                  trigger="click"
                >
                  <button
                    type="button"
                    ref={(element) => {
                      colorButtonRefs.current[index] = element;
                    }}
                    className={`w-9 h-9 rounded-lg border-2 cursor-pointer transition-all ${
                      isOpen
                        ? "border-blue-500 dark:border-blue-400 ring-2 ring-blue-200 dark:ring-blue-900/50 scale-110"
                        : `${borders.default} hover:scale-105 hover:shadow-md`
                    }`}
                    style={{ backgroundColor: hexNormalized }}
                    title={`Color ${index + 1}: ${hexNormalized}`}
                    aria-label={`Color ${index + 1}`}
                  />
                </ColorPicker>
                <div className="flex items-center justify-center gap-1 leading-none">
                  <span className={`${typography.caption} ${textColors.tertiary} font-mono leading-none`}>
                    {hexNormalized.slice(0, 7)}
                  </span>
                  {isEyeDropperSupported && (
                    <button
                      type="button"
                      onClick={() => handleEyeDropperPickForIndex(index)}
                      disabled={isEyeDropperActive}
                      className={`inline-flex h-5 w-5 items-center justify-center rounded ${textColors.tertiary} hover:text-blue-500 dark:hover:text-blue-400 transition-colors cursor-pointer`}
                      title={`Pick color ${index + 1} from screen`}
                      aria-label={`Pick color ${index + 1} from screen`}
                    >
                      <EyeDropperIcon size={11} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Palette picker — prominent full-width button with color preview */}
      <div className="relative mt-2" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setShowSuggestions(!showSuggestions)}
          ref={suggestionsButtonRef}
          className={`w-full flex items-center gap-2.5 px-3 py-1.5 ${surfaces.secondary} border ${borders.default} rounded-xl ${surfaces.hover} transition-all cursor-pointer group`}
          title="Browse color palettes"
        >
          <BulbOutlined className={`${textColors.secondary} group-hover:text-amber-400 transition-colors`} style={{ fontSize: '16px' }} />
          <span className={`${typography.bodySmall} ${textColors.primary} flex-1 text-left`}>Browse Palettes</span>
          {savedPalettes.length > 0 && (
            <span className={`shrink-0 px-2 py-0.5 rounded-full border ${borders.default} ${typography.caption} ${textColors.secondary}`}>
              {savedPalettes.length} Saved
            </span>
          )}
          <div className="flex gap-0.5">
            {colors.map((c, i) => (
              <div
                key={i}
                className={`w-4 h-4 ${i === 0 ? "rounded-l" : ""} ${i === colors.length - 1 ? "rounded-r" : ""}`}
                style={{ backgroundColor: normalizeColorValue(c) }}
              />
            ))}
          </div>
          <svg className={`w-4 h-4 ${textColors.tertiary} transition-transform ${showSuggestions ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Suggestions dropdown */}
        {showSuggestions && (
          <div
            className={`absolute left-0 right-0 ${suggestionsDropdown.openUp ? "bottom-12" : "top-12"} ${surfaces.elevated} border ${borders.default} rounded-xl shadow-lg p-3 z-10 overflow-y-auto`}
            style={{ maxHeight: `${suggestionsDropdown.maxHeight}px` }}
          >
            <div className="space-y-3">
              {savedPalettes.length > 0 && (
                <div>
                  <div className={`${typography.caption} ${textColors.secondary} mb-1.5`}>
                    Saved palettes ({savedPalettes.length}/{MAX_SAVED_PALETTES})
                  </div>
                  <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))" }}>
                    {savedPalettes.map((palette, paletteIdx) => (
                      <div
                        key={`saved-${paletteIdx}`}
                        className="relative group"
                      >
                        <button
                          type="button"
                          onClick={() => applySuggestion(palette)}
                          className={`flex gap-0.5 w-full p-1.5 rounded-lg transition-colors border border-transparent ${surfaces.hover} cursor-pointer`}
                          title="Apply this palette"
                        >
                          {palette.map((color, colorIdx) => (
                            <div
                              key={colorIdx}
                              className={`h-5 flex-1 ${colorIdx === 0 ? "rounded-l-md" : ""} ${colorIdx === palette.length - 1 ? "rounded-r-md" : ""}`}
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemovePalette(paletteIdx)}
                          className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 dark:bg-white/30 text-white cursor-pointer"
                          title="Remove saved palette"
                        >
                          <CloseOutlined style={{ fontSize: 7 }} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className={`${savedPalettes.length > 0 ? `border-t ${borders.default} pt-3` : ""}`}>
                <div className={`${typography.caption} ${textColors.secondary} mb-1.5`}>
                  Palette suggestions
                </div>
                <div className="space-y-1">
                  {paletteSuggestions.map((suggestion, index) => (
                    <button
                      type="button"
                      key={`${suggestion.name}-${index}`}
                      onClick={() => applySuggestion(suggestion.colors)}
                      className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors border border-transparent ${surfaces.hover}`}
                    >
                      <div className="flex gap-0.5">
                        {suggestion.colors.map((color, colorIndex) => (
                          <div
                            key={colorIndex}
                            className={`w-5 h-5 ${colorIndex === 0 ? "rounded-l-md" : ""} ${colorIndex === suggestion.colors.length - 1 ? "rounded-r-md" : ""}`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                      <span className={`${typography.bodySmall} ${textColors.primary} flex-1`}>{suggestion.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {help && <div className={`mt-2 ${typography.caption} ${textColors.tertiary}`}>{help}</div>}
      {infoText && <div className={`mt-1 ${typography.caption} ${textColors.tertiary}`}>{infoText}</div>}
    </div>
  );
}; 